import React from 'react';
import cn from '@/utils/cn';
import { AIPolicySnapshot, UsageReport } from './types';
import { formatTokens } from './usagePalette';

interface StatProps {
  label: string;
  sub: string;
  value: string;
  warning?: boolean;
}

const cellClass = 'tw-box-border tw-min-w-0 tw-bg-spa-bg-secondary tw-px-5 tw-py-4';

const Stat = ({
  label, sub, value, warning = false,
}: StatProps) => (
  <div className={cellClass}>
    <div
      className={cn(
        'tw-flex tw-items-center tw-gap-1.5 tw-text-[0.6875rem] tw-font-semibold',
        'tw-uppercase tw-tracking-wider tw-text-spa-secondary',
      )}
    >
      {warning && <span aria-hidden="true" className="tw-h-2 tw-w-2 tw-shrink-0 tw-rounded-full tw-bg-spa-warning" />}
      {label}
    </div>
    <div className="tw-mt-1.5 tw-text-[1.75rem] tw-font-semibold tw-leading-tight tw-text-spa-heading">{value}</div>
    <div className="tw-mt-1 tw-text-xs tw-text-spa-tertiary">{sub}</div>
  </div>
);

const SkeletonStat = () => (
  <div aria-hidden="true" className={cn(cellClass, 'tw-animate-pulse')}>
    <div className="tw-h-3 tw-w-24 tw-rounded tw-bg-spa-bg-skeleton" />
    <div className="tw-mt-2.5 tw-h-7 tw-w-20 tw-rounded tw-bg-spa-bg-skeleton" />
    <div className="tw-mt-2 tw-h-3 tw-w-28 tw-rounded tw-bg-spa-bg-skeleton-subtle" />
  </div>
);

interface SummaryStripProps {
  days: number;
  snapshot?: AIPolicySnapshot;
  usage?: UsageReport;
}

// Same raised strip as AI Safety's: the 1px grid gap shows the container's border colour through
// as the cell dividers.
const SummaryStrip = ({ days, snapshot, usage }: SummaryStripProps) => {
  const limited = usage?.throttled.length ?? 0;
  const active = snapshot?.status === 'active';

  return (
    <div
      aria-busy={!usage || !snapshot}
      aria-label="Summary"
      className={cn(
        'tw-mb-7 tw-grid tw-grid-cols-2 tw-gap-px tw-overflow-hidden tw-rounded-lg lg:tw-grid-cols-4',
        'tw-border tw-border-solid tw-border-spa-border-secondary tw-bg-spa-border-secondary',
      )}
      role="region"
    >
      {usage && snapshot ? (
        <>
          <Stat
            label={`Tokens · ${days}d`}
            sub="Volume, not spend"
            value={formatTokens(usage.totals.tokens)}
          />
          <Stat
            label={`Requests · ${days}d`}
            sub={`${usage.top_consumers.length} identities`}
            value={usage.totals.requests.toLocaleString()}
          />
          <Stat
            label="Limited now"
            sub={limited > 0 ? 'Refused by the gateway' : 'Nobody is being refused'}
            value={String(limited)}
            warning={limited > 0}
          />
          <Stat
            label="Limiter"
            sub={active ? 'Limits enforced' : 'Limits not enforced'}
            value={snapshot.status ?? 'unknown'}
            warning={!active}
          />
        </>
      ) : (
        Array.from({ length: 4 }, (_, index) => <SkeletonStat key={index} />)
      )}
    </div>
  );
};

export default SummaryStrip;
